import React, { useState, useMemo } from 'react';
import { 
  Search, 
  BookOpen, 
  Users, 
  Lightbulb, 
  Navigation, 
  Layers, 
  Brain, 
  Database, 
  Shield, 
  Settings, 
  HelpCircle, 
  AlertTriangle, 
  FileText, 
  Mail, 
  ExternalLink,
  ChevronRight,
  X,
  Menu,
  Home,
  Star,
  Clock,
  CheckCircle,
  Info,
  AlertCircle,
  BarChart3,
  Target,
  TrendingUp
} from 'lucide-react';
import { useHelp } from '../../context/HelpContext';

const GlobalHelp = () => {
  const { isGlobalHelpOpen, closeGlobalHelp } = useHelp();
  const [activeSection, setActiveSection] = useState('welcome');
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Help sections data
  const sections = [
    { id: 'welcome', title: 'Welcome & What This App Does', icon: Home },
    { id: 'getting-started', title: 'Getting Started (by Role)', icon: Users },
    { id: 'core-concepts', title: 'Core Concepts & Terminology', icon: Lightbulb },
    { id: 'navigation', title: 'Navigation Guide', icon: Navigation },
    { id: 'modules', title: 'Modules Overview', icon: Layers },
    { id: 'ai-intelligence', title: 'AI & Intelligence', icon: Brain },
    { id: 'data-integrations', title: 'Data & Integrations', icon: Database },
    { id: 'security', title: 'Security, Privacy & Compliance', icon: Shield },
    { id: 'administration', title: 'Administration', icon: Settings },
    { id: 'faq', title: 'FAQs', icon: HelpCircle },
    { id: 'troubleshooting', title: 'Troubleshooting', icon: AlertTriangle },
    { id: 'release-notes', title: 'Release Notes / What\'s New', icon: FileText },
    { id: 'contact-support', title: 'Contact & Support', icon: Mail },
    { id: 'legal-policies', title: 'Legal & Policies', icon: Shield }
  ];

  // Search functionality
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    
    const query = searchQuery.toLowerCase();
    const results = [];
    
    // Search through sections and their content
    sections.forEach(section => {
      if (section.title.toLowerCase().includes(query)) {
        results.push({
          type: 'section',
          id: section.id,
          title: section.title,
          section: 'Navigation'
        });
      }
    });

    // Add FAQ results
    const faqItems = getFAQItems();
    faqItems.forEach(faq => {
      if (faq.question.toLowerCase().includes(query) || 
          faq.answer.toLowerCase().includes(query)) {
        results.push({
          type: 'faq',
          id: faq.id,
          title: faq.question,
          section: 'FAQs'
        });
      }
    });

    return results.slice(0, 10); // Limit to 10 results
  }, [searchQuery]);

  // FAQ data
  const getFAQItems = () => [
    {
      id: 'faq-1',
      question: 'How do I create my first asset?',
      answer: 'Navigate to Assets → Asset Registry and click "New Asset". Fill in the required fields like Asset Name, Category, and Location. You can also import assets in bulk using the Import feature.'
    },
    {
      id: 'faq-2',
      question: 'What is the difference between a contract and a contract template?',
      answer: 'A contract template is a reusable document with predefined clauses and terms. A contract is an actual agreement created from a template or from scratch, with specific parties, dates, and values.'
    },
    {
      id: 'faq-3',
      question: 'How does AI risk scoring work?',
      answer: 'Our AI analyzes contract clauses against a database of known risks and legal precedents. It assigns risk scores based on factors like liability exposure, compliance requirements, and market standards.'
    },
    {
      id: 'faq-4',
      question: 'Can I customize user roles and permissions?',
      answer: 'Yes, Super Admins can create custom roles with specific permissions. Go to System → User Management → Roles to create or modify role permissions.'
    },
    {
      id: 'faq-5',
      question: 'How do I export data from the system?',
      answer: 'Most data tables have an Export button. You can export to Excel, CSV, or PDF formats. For bulk exports, use the Reports section under Insights.'
    },
    {
      id: 'faq-6',
      question: 'What file formats are supported for document upload?',
      answer: 'We support PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, and image files (JPG, PNG, GIF). Maximum file size is 50MB per file.'
    },
    {
      id: 'faq-7',
      question: 'How do I set up automated contract renewals?',
      answer: 'Go to Contracts → Renewal Alerts and configure renewal rules. You can set alerts for 30, 60, or 90 days before expiration and choose notification methods.'
    },
    {
      id: 'faq-8',
      question: 'Can I integrate with external systems?',
      answer: 'Yes, we support integrations with ERP systems (SAP, Oracle), CRM platforms (Salesforce), and SSO providers. Contact support for specific integration requirements.'
    },
    {
      id: 'faq-9',
      question: 'How do I track contract compliance?',
      answer: 'Use the Compliance Tracking module to monitor contract obligations, deadlines, and requirements. Set up automated reminders and generate compliance reports.'
    },
    {
      id: 'faq-10',
      question: 'What happens to my data if I cancel my subscription?',
      answer: 'Your data is retained for 90 days after cancellation. You can export all your data during this period. After 90 days, data is permanently deleted according to our data retention policy.'
    },
    {
      id: 'faq-11',
      question: 'How do I reset my password?',
      answer: 'Click "Forgot Password" on the login page and enter your email address. You\'ll receive a password reset link within 5 minutes. Check your spam folder if you don\'t see it.'
    },
    {
      id: 'faq-12',
      question: 'Can I collaborate on contracts with external parties?',
      answer: 'Yes, use the Collaboration Hub to share contracts with external users. You can control access levels and track all changes and comments.'
    }
  ];

  // Release notes data
  const getReleaseNotes = () => [
    {
      version: 'v2.1.0',
      date: '2024-01-15',
      type: 'feature',
      title: 'Enhanced AI Contract Analysis',
      description: 'New AI-powered contract analysis with improved risk scoring and clause identification. Added support for 15+ languages in document translation.',
      features: [
        'Advanced risk scoring algorithm',
        'Multi-language document translation',
        'Improved clause extraction accuracy',
        'New contract comparison tool'
      ]
    },
    {
      version: 'v2.0.5',
      date: '2024-01-08',
      type: 'improvement',
      title: 'Performance Improvements',
      description: 'Significant performance enhancements across all modules with faster loading times and improved search functionality.',
      features: [
        '50% faster page load times',
        'Enhanced search with filters',
        'Improved mobile responsiveness',
        'Better error handling'
      ]
    },
    {
      version: 'v2.0.4',
      date: '2023-12-20',
      type: 'fix',
      title: 'Bug Fixes and Security Updates',
      description: 'Critical security patches and bug fixes for improved system stability.',
      features: [
        'Fixed document upload issues',
        'Resolved PDF generation errors',
        'Security vulnerability patches',
        'Improved data validation'
      ]
    }
  ];

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchResultClick = (result) => {
    if (result.type === 'section') {
      setActiveSection(result.id);
    } else if (result.type === 'faq') {
      setActiveSection('faq');
    }
    setSearchQuery('');
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'welcome':
        return (
          <div className="help-content">
            <h2>Welcome to CAM360</h2>
            <div className="help-intro">
              <p>
                CAM360 is a comprehensive asset and contract management platform designed to streamline 
                your organization's asset lifecycle and contract processes. Our platform combines powerful asset 
                tracking capabilities with intelligent contract management, enhanced by AI-driven insights and 
                automation.
              </p>
              
              <div className="help-benefits">
                <h3>Key Benefits</h3>
                <ul>
                  <li><strong>Centralized Management:</strong> Single platform for all assets and contracts</li>
                  <li><strong>AI-Powered Insights:</strong> Intelligent risk analysis and predictive analytics</li>
                  <li><strong>Automated Workflows:</strong> Streamlined approval processes and renewals</li>
                  <li><strong>Compliance Tracking:</strong> Built-in compliance monitoring and reporting</li>
                  <li><strong>Collaborative Tools:</strong> Team collaboration and external stakeholder management</li>
                </ul>
              </div>

              <div className="help-audience">
                <h3>Who This App Is For</h3>
                <p>
                  CAM360 serves organizations of all sizes across various industries including 
                  manufacturing, real estate, healthcare, education, and government. Whether you're managing 
                  physical assets, service contracts, or complex legal agreements, our platform adapts to 
                  your needs.
                </p>
              </div>
            </div>
          </div>
        );

      case 'getting-started':
        return (
          <div className="help-content">
            <h2>Getting Started by Role</h2>
            
            <div className="role-sections">
              <div className="role-section">
                <h3><Users className="role-icon" /> Super Admin</h3>
                <div className="checklist">
                  <h4>First Day Tasks:</h4>
                  <ul>
                    <li>✓ Configure tenant settings and branding</li>
                    <li>✓ Set up user roles and permissions</li>
                    <li>✓ Configure system integrations</li>
                    <li>✓ Import initial data (assets/contracts)</li>
                    <li>✓ Set up approval workflows</li>
                  </ul>
                </div>
              </div>

              <div className="role-section">
                <h3><Users className="role-icon" /> Admin</h3>
                <div className="checklist">
                  <h4>First Day Tasks:</h4>
                  <ul>
                    <li>✓ Review and configure user permissions</li>
                    <li>✓ Set up asset categories and templates</li>
                    <li>✓ Configure contract templates</li>
                    <li>✓ Set up notification preferences</li>
                    <li>✓ Train team members on basic features</li>
                  </ul>
                </div>
              </div>

              <div className="role-section">
                <h3><Users className="role-icon" /> Finance</h3>
                <div className="checklist">
                  <h4>First Day Tasks:</h4>
                  <ul>
                    <li>✓ Set up financial valuation methods</li>
                    <li>✓ Configure budget tracking and alerts</li>
                    <li>✓ Import financial data and contracts</li>
                    <li>✓ Set up automated reporting</li>
                    <li>✓ Configure approval workflows for expenses</li>
                  </ul>
                </div>
              </div>

              <div className="role-section">
                <h3><Users className="role-icon" /> Legal</h3>
                <div className="checklist">
                  <h4>First Day Tasks:</h4>
                  <ul>
                    <li>✓ Review and customize contract templates</li>
                    <li>✓ Set up compliance tracking rules</li>
                    <li>✓ Configure risk scoring parameters</li>
                    <li>✓ Set up legal review workflows</li>
                    <li>✓ Import existing legal documents</li>
                  </ul>
                </div>
              </div>

              <div className="role-section">
                <h3><Users className="role-icon" /> Standard User</h3>
                <div className="checklist">
                  <h4>First Day Tasks:</h4>
                  <ul>
                    <li>✓ Complete user profile setup</li>
                    <li>✓ Explore assigned modules and features</li>
                    <li>✓ Review dashboard and key metrics</li>
                    <li>✓ Set up personal preferences</li>
                    <li>✓ Complete basic training modules</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'core-concepts':
        return (
          <div className="help-content">
            <h2>Core Concepts & Terminology</h2>
            
            <div className="glossary">
              <div className="glossary-section">
                <h3>Assets</h3>
                <dl>
                  <dt>Asset</dt>
                  <dd>Any physical or digital item of value owned by your organization that requires tracking and management.</dd>
                  
                  <dt>Asset Category</dt>
                  <dd>A classification system for organizing assets by type, function, or department.</dd>
                  
                  <dt>Asset Lifecycle</dt>
                  <dd>The complete journey of an asset from acquisition to disposal, including maintenance, upgrades, and depreciation.</dd>
                  
                  <dt>Asset Valuation</dt>
                  <dd>The process of determining the monetary value of an asset using various methods (cost, market, income).</dd>
                </dl>
              </div>

              <div className="glossary-section">
                <h3>Contracts</h3>
                <dl>
                  <dt>Contract</dt>
                  <dd>A legally binding agreement between parties that defines terms, conditions, and obligations.</dd>
                  
                  <dt>Contract Template</dt>
                  <dd>A reusable document template with predefined clauses and terms for creating new contracts.</dd>
                  
                  <dt>Contract Lifecycle</dt>
                  <dd>The stages of a contract from creation through execution, monitoring, and renewal or termination.</dd>
                  
                  <dt>Clause</dt>
                  <dd>A specific provision or section within a contract that addresses particular terms or conditions.</dd>
                </dl>
              </div>

              <div className="glossary-section">
                <h3>Workflows</h3>
                <dl>
                  <dt>Approval Workflow</dt>
                  <dd>A predefined sequence of approvals required for documents, purchases, or decisions.</dd>
                  
                  <dt>Renewal Alert</dt>
                  <dd>Automated notifications sent before contract or license expiration dates.</dd>
                  
                  <dt>Compliance Tracking</dt>
                  <dd>Monitoring and reporting on adherence to contractual obligations and regulatory requirements.</dd>
                </dl>
              </div>
            </div>
          </div>
        );

      case 'navigation':
        return (
          <div className="help-content">
            <h2>Navigation Guide</h2>
            
            <div className="navigation-guide">
              <h3>Sidebar Organization</h3>
              <p>The sidebar is organized into logical groups and menus:</p>
              
              <div className="nav-group">
                <h4>📊 Dashboard</h4>
                <p>Overview of key metrics, recent activities, and quick access to important features.</p>
              </div>

              <div className="nav-group">
                <h4>🏢 Assets</h4>
                <ul>
                  <li><strong>Asset Registry:</strong> Complete asset inventory and management</li>
                  <li><strong>Asset Lifecycle:</strong> Track asset stages and transitions</li>
                  <li><strong>Asset Categorization:</strong> Organize assets by type and function</li>
                  <li><strong>Financial Valuation:</strong> Asset valuation and depreciation tracking</li>
                </ul>
              </div>

              <div className="nav-group">
                <h4>📋 Contracts</h4>
                <ul>
                  <li><strong>Contract Management:</strong> Create, edit, and manage contracts</li>
                  <li><strong>Contract Templates:</strong> Reusable contract templates</li>
                  <li><strong>Clause Manager:</strong> Manage contract clauses and terms</li>
                  <li><strong>E-Signature:</strong> Digital signature workflows</li>
                  <li><strong>Renewal Alerts:</strong> Contract renewal management</li>
                </ul>
              </div>

              <div className="nav-group">
                <h4>🤖 AI & Intelligence</h4>
                <ul>
                  <li><strong>AI Insights:</strong> AI-powered analytics and recommendations</li>
                  <li><strong>Contract Drafting:</strong> AI-assisted contract creation</li>
                  <li><strong>Search & Q&A:</strong> Natural language search capabilities</li>
                  <li><strong>Predictive Analytics:</strong> Forecasting and trend analysis</li>
                </ul>
              </div>

              <h3>Finding Features Quickly</h3>
              <div className="quick-tips">
                <div className="tip">
                  <Search className="tip-icon" />
                  <div>
                    <h4>Global Search</h4>
                    <p>Use the search bar in the top navigation to quickly find assets, contracts, or any content.</p>
                  </div>
                </div>
                
                <div className="tip">
                  <Star className="tip-icon" />
                  <div>
                    <h4>Saved Views</h4>
                    <p>Save frequently used filters and views for quick access to specific data sets.</p>
                  </div>
                </div>
                
                <div className="tip">
                  <BookOpen className="tip-icon" />
                  <div>
                    <h4>Recent Items</h4>
                    <p>Access recently viewed items from the dashboard or sidebar quick access menu.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'modules':
        return (
          <div className="help-content">
            <h2>Modules Overview</h2>
            
            <div className="modules-grid">
              <div className="module-card">
                <h3><Layers className="module-icon" /> Assets Module</h3>
                <p><strong>What it covers:</strong> Complete asset lifecycle management from acquisition to disposal.</p>
                <p><strong>Typical tasks:</strong></p>
                <ul>
                  <li>Register and categorize assets</li>
                  <li>Track maintenance schedules</li>
                  <li>Monitor asset performance</li>
                  <li>Manage asset assignments</li>
                  <li>Generate asset reports</li>
                </ul>
                <div className="module-links">
                  <a href="#" className="help-link">Asset Registry Help <ExternalLink size={14} /></a>
                  <a href="#" className="help-link">Asset Lifecycle Help <ExternalLink size={14} /></a>
                </div>
              </div>

              <div className="module-card">
                <h3><FileText className="module-icon" /> Contracts Module</h3>
                <p><strong>What it covers:</strong> End-to-end contract management and lifecycle tracking.</p>
                <p><strong>Typical tasks:</strong></p>
                <ul>
                  <li>Create and edit contracts</li>
                  <li>Manage contract templates</li>
                  <li>Track contract compliance</li>
                  <li>Handle renewals and terminations</li>
                  <li>Generate contract reports</li>
                </ul>
                <div className="module-links">
                  <a href="#" className="help-link">Contract Management Help <ExternalLink size={14} /></a>
                  <a href="#" className="help-link">Contract Templates Help <ExternalLink size={14} /></a>
                </div>
              </div>

              <div className="module-card">
                <h3><Brain className="module-icon" /> AI & Intelligence Module</h3>
                <p><strong>What it covers:</strong> AI-powered insights, automation, and intelligent document processing.</p>
                <p><strong>Typical tasks:</strong></p>
                <ul>
                  <li>Analyze contract risks</li>
                  <li>Extract data from documents</li>
                  <li>Generate insights and predictions</li>
                  <li>Automate routine tasks</li>
                  <li>Search using natural language</li>
                </ul>
                <div className="module-links">
                  <a href="#" className="help-link">AI Insights Help <ExternalLink size={14} /></a>
                  <a href="#" className="help-link">Contract Drafting Help <ExternalLink size={14} /></a>
                </div>
              </div>

              <div className="module-card">
                <h3><BarChart3 className="module-icon" /> Insights & Reports Module</h3>
                <p><strong>What it covers:</strong> Analytics, reporting, and business intelligence features.</p>
                <p><strong>Typical tasks:</strong></p>
                <ul>
                  <li>Generate custom reports</li>
                  <li>Create interactive dashboards</li>
                  <li>Analyze trends and patterns</li>
                  <li>Export data for external analysis</li>
                  <li>Schedule automated reports</li>
                </ul>
                <div className="module-links">
                  <a href="#" className="help-link">Reports Help <ExternalLink size={14} /></a>
                  <a href="#" className="help-link">Analytics Help <ExternalLink size={14} /></a>
                </div>
              </div>

              <div className="module-card">
                <h3><Settings className="module-icon" /> System & Settings Module</h3>
                <p><strong>What it covers:</strong> System configuration, user management, and administrative functions.</p>
                <p><strong>Typical tasks:</strong></p>
                <ul>
                  <li>Manage users and roles</li>
                  <li>Configure system settings</li>
                  <li>Set up integrations</li>
                  <li>Monitor system health</li>
                  <li>Manage data and backups</li>
                </ul>
                <div className="module-links">
                  <a href="#" className="help-link">User Management Help <ExternalLink size={14} /></a>
                  <a href="#" className="help-link">System Settings Help <ExternalLink size={14} /></a>
                </div>
              </div>
            </div>
          </div>
        );

      case 'ai-intelligence':
        return (
          <div className="help-content">
            <h2>AI & Intelligence</h2>
            
            <div className="ai-overview">
              <h3>What AI Can Do</h3>
              <div className="ai-capabilities">
                <div className="capability">
                  <Brain className="capability-icon" />
                  <div>
                    <h4>Document Extraction</h4>
                    <p>Automatically extract key information from contracts, invoices, and other documents with high accuracy.</p>
                  </div>
                </div>
                
                <div className="capability">
                  <Target className="capability-icon" />
                  <div>
                    <h4>Risk Scoring</h4>
                    <p>Analyze contract clauses and identify potential risks based on legal precedents and industry standards.</p>
                  </div>
                </div>
                
                <div className="capability">
                  <Search className="capability-icon" />
                  <div>
                    <h4>Natural Language Search</h4>
                    <p>Search through documents using everyday language instead of specific keywords or tags.</p>
                  </div>
                </div>
                
                <div className="capability">
                  <TrendingUp className="capability-icon" />
                  <div>
                    <h4>Predictive Analytics</h4>
                    <p>Forecast trends, identify patterns, and predict outcomes based on historical data and current conditions.</p>
                  </div>
                </div>
                
                <div className="capability">
                  <FileText className="capability-icon" />
                  <div>
                    <h4>Contract Summarization</h4>
                    <p>Generate concise summaries of complex contracts highlighting key terms, obligations, and risks.</p>
                  </div>
                </div>
              </div>

              <div className="ai-responsible-use">
                <h3>Responsible Use & Limitations</h3>
                <div className="warning-box">
                  <AlertCircle className="warning-icon" />
                  <div>
                    <h4>Important Considerations</h4>
                    <ul>
                      <li><strong>Human Review Required:</strong> AI-generated content should always be reviewed by qualified professionals</li>
                      <li><strong>Data Privacy:</strong> All AI processing follows strict data protection protocols</li>
                      <li><strong>Accuracy Limitations:</strong> AI results are probabilistic and may require validation</li>
                      <li><strong>Legal Compliance:</strong> AI insights are supplementary and do not replace legal advice</li>
                      <li><strong>Continuous Learning:</strong> AI models improve over time but may have knowledge gaps</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'faq':
        return (
          <div className="help-content">
            <h2>Frequently Asked Questions</h2>
            
            <div className="faq-list">
              {getFAQItems().map((faq) => (
                <div key={faq.id} className="faq-item">
                  <h3 className="faq-question">{faq.question}</h3>
                  <p className="faq-answer">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        );

      case 'release-notes':
        return (
          <div className="help-content">
            <h2>Release Notes / What's New</h2>
            
            <div className="release-notes">
              {getReleaseNotes().map((release, index) => (
                <div key={index} className="release-item">
                  <div className="release-header">
                    <span className={`release-type ${release.type}`}>
                      {release.type === 'feature' && <Star size={16} />}
                      {release.type === 'improvement' && <CheckCircle size={16} />}
                      {release.type === 'fix' && <AlertCircle size={16} />}
                      {release.type}
                    </span>
                    <h3>{release.title}</h3>
                    <span className="release-version">{release.version}</span>
                    <span className="release-date">{release.date}</span>
                  </div>
                  <p className="release-description">{release.description}</p>
                  <ul className="release-features">
                    {release.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );

      case 'contact-support':
        return (
          <div className="help-content">
            <h2>Contact & Support</h2>
            
            <div className="support-info">
              <div className="support-methods">
                <div className="support-method">
                  <Mail className="support-icon" />
                  <div>
                    <h3>Email Support</h3>
                    <p><strong>support@assetcontractpro.com</strong></p>
                    <p>Response time: Within 4 hours during business hours</p>
                  </div>
                </div>
                
                <div className="support-method">
                  <Clock className="support-icon" />
                  <div>
                    <h3>Business Hours</h3>
                    <p><strong>Monday - Friday:</strong> 8:00 AM - 6:00 PM EST</p>
                    <p><strong>Saturday:</strong> 9:00 AM - 2:00 PM EST</p>
                    <p><strong>Sunday:</strong> Closed</p>
                  </div>
                </div>
                
                <div className="support-method">
                  <Shield className="support-icon" />
                  <div>
                    <h3>SLA Expectations</h3>
                    <p><strong>Critical Issues:</strong> 2 hours</p>
                    <p><strong>High Priority:</strong> 8 hours</p>
                    <p><strong>Medium Priority:</strong> 24 hours</p>
                    <p><strong>Low Priority:</strong> 72 hours</p>
                  </div>
                </div>
              </div>

              <div className="escalation-path">
                <h3>Escalation Path</h3>
                <ol>
                  <li><strong>Level 1:</strong> Submit support ticket via email</li>
                  <li><strong>Level 2:</strong> If not resolved in 24 hours, request escalation</li>
                  <li><strong>Level 3:</strong> For critical issues, contact your account manager</li>
                  <li><strong>Emergency:</strong> Call +1-800-HELP-ACO for critical system outages</li>
                </ol>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="help-content">
            <h2>Section Coming Soon</h2>
            <p>This section is under development. Please check back soon for comprehensive information.</p>
          </div>
        );
    }
  };

  if (!isGlobalHelpOpen) return null;

  return (
    <div className="global-help-overlay">
      <div className="global-help-container">
        {/* Header */}
        <div className="help-header">
          <div className="help-header-left">
            <BookOpen className="help-logo" />
            <h1>Global Help Center</h1>
          </div>
          <div className="help-header-right">
            <button 
              className="help-close-btn"
              onClick={closeGlobalHelp}
              title="Close Help Center"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="help-body">
          {/* Sidebar */}
          <div className="help-sidebar">
            <div className="help-search">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder="Search help content..."
                value={searchQuery}
                onChange={handleSearch}
                className="help-search-input"
              />
            </div>

            {searchQuery && searchResults.length > 0 && (
              <div className="search-results">
                <h4>Search Results</h4>
                {searchResults.map((result, index) => (
                  <button
                    key={index}
                    className="search-result-item"
                    onClick={() => handleSearchResultClick(result)}
                  >
                    <div className="search-result-title">{result.title}</div>
                    <div className="search-result-section">{result.section}</div>
                  </button>
                ))}
              </div>
            )}

            <nav className="help-nav">
              <button
                className="mobile-menu-btn"
                onClick={() => setShowMobileMenu(!showMobileMenu)}
              >
                <Menu size={20} />
                Menu
              </button>
              
              <div className={`help-nav-list ${showMobileMenu ? 'show' : ''}`}>
                {sections.map((section) => {
                  const IconComponent = section.icon;
                  return (
                    <button
                      key={section.id}
                      className={`help-nav-item ${activeSection === section.id ? 'active' : ''}`}
                      onClick={() => {
                        setActiveSection(section.id);
                        setShowMobileMenu(false);
                      }}
                    >
                      <IconComponent size={18} />
                      <span>{section.title}</span>
                      <ChevronRight size={16} />
                    </button>
                  );
                })}
              </div>
            </nav>
          </div>

          {/* Main Content */}
          <div className="help-main">
            {renderContent()}
            
            {/* Feedback Section */}
            <div className="help-feedback">
              <h3>Was this helpful?</h3>
              <div className="feedback-buttons">
                <button className="feedback-btn positive">
                  <CheckCircle size={16} />
                  Yes
                </button>
                <button className="feedback-btn negative">
                  <X size={16} />
                  No
                </button>
              </div>
              <textarea
                placeholder="Tell us how we can improve this help content..."
                className="feedback-textarea"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalHelp;
